// Money out: expenses, vendor bills, driver pay, transfers and other income.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeftRight, Banknote, Plus, ReceiptText, ShieldCheck, Wallet } from "lucide-react";

import { api, formatApiError } from "../../lib/api";
import { CAPABILITIES, EXPENSE_CATEGORIES, PAYMENT_METHODS, can, docTypeLabel, idemHeaders, todayISO }
  from "../../lib/books";
import { Button, Empty, Field, Loading, Modal, Money, Pill, Section, Table, inputClass } from "./shared";

const FORMS = {
  expense: "Record an expense",
  bill: "Record a vendor bill",
  transfer: "Move money between accounts",
  other_income: "Record other income",
  owner: "Owner or loan movement",
  driver_accrual: "Driver pay owed",
  driver_payment: "Pay a driver",
  insurance: "Insurance claim approved",
};

export default function ExpensesTab({ status, accounts, onChanged }) {
  const [tab, setTab] = useState("recent");
  const [documents, setDocuments] = useState([]);
  const [bills, setBills] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const canExpense = can(status, CAPABILITIES.recordExpense);
  const canPost = can(status, CAPABILITIES.post);
  const canPay = can(status, CAPABILITIES.recordPayment);

  const load = async () => {
    setLoading(true);
    try {
      const [docs, aging, vend] = await Promise.all([
        api.get("/finance/documents", { params: {
          doc_type: "expense,bill,bill_payment,transfer,other_income,financing,driver_accrual,driver_payment," +
                    "insurance_claim,insurance_receipt", limit: 100 } }),
        api.get("/finance/reports/ap-aging"),
        api.get("/finance/vendors"),
      ]);
      setDocuments(docs.data || []);
      setBills(aging.data);
      setVendors(vend.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  const done = () => { setForm(null); load(); onChanged?.(); };

  if (loading) return <Loading label="Reading expenses..." />;

  return (
    <div className="space-y-4" data-testid="books-expenses">
      <div className="flex flex-wrap gap-2">
        {canExpense && <Button onClick={() => setForm("expense")} data-testid="books-new-expense">
          <ReceiptText className="w-4 h-4" /> Expense
        </Button>}
        {canExpense && <Button variant="secondary" onClick={() => setForm("bill")} data-testid="books-new-bill">
          <Plus className="w-4 h-4" /> Vendor bill
        </Button>}
        {canPay && <Button variant="secondary" onClick={() => setForm("transfer")} data-testid="books-new-transfer">
          <ArrowLeftRight className="w-4 h-4" /> Transfer
        </Button>}
        {canPay && <Button variant="secondary" onClick={() => setForm("other_income")}>
          <Wallet className="w-4 h-4" /> Other income
        </Button>}
        {canPost && <Button variant="secondary" onClick={() => setForm("owner")}>
          <Banknote className="w-4 h-4" /> Owner / loan
        </Button>}
        {canPost && <Button variant="secondary" onClick={() => setForm("driver_accrual")}
                            data-testid="books-new-driver-accrual">Driver pay owed</Button>}
        {canExpense && <Button variant="secondary" onClick={() => setForm("driver_payment")}>Pay a driver</Button>}
        {canPost && <Button variant="secondary" onClick={() => setForm("insurance")}>
          <ShieldCheck className="w-4 h-4" /> Insurance claim
        </Button>}
      </div>

      <div className="flex gap-2 text-sm">
        {[["recent", "Recent"], ["payables", "Unpaid bills"], ["vendors", "Vendors"]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`books-expenses-tab-${key}`}
                  className={`px-3 py-1.5 rounded-lg border ${tab === key
                    ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-700"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "recent" && (
        <Section title="Recent entries" testId="books-expense-list">
          <Table head={["Document", "Date", "Details", { key: "amount", label: "Amount", align: "right" }]}
                 empty={documents.length === 0 && <Empty>Nothing recorded yet.</Empty>}>
            {documents.map((doc) => (
              <tr key={doc.id} data-testid={`books-doc-${doc.number}`}>
                <td className="py-2 pr-3">
                  <div className="font-medium text-slate-900">{doc.number}</div>
                  <Pill status={doc.status}>{docTypeLabel(doc.doc_type)}</Pill>
                </td>
                <td className="py-2 pr-3 text-slate-600">{doc.date}</td>
                <td className="py-2 pr-3 text-slate-600">
                  {doc.description}
                  {doc.counterparty_name && <div className="text-xs text-slate-500">{doc.counterparty_name}</div>}
                </td>
                <td className="py-2 pr-3 text-right"><Money minor={doc.total_minor} /></td>
              </tr>
            ))}
          </Table>
        </Section>
      )}

      {tab === "payables" && (
        <Section title="Unpaid bills and driver pay" testId="books-payables">
          <Table head={["Document", "Due", "Who", { key: "open", label: "Open", align: "right" }, ""]}
                 empty={(bills?.rows || []).length === 0 && <Empty>Nothing is unpaid.</Empty>}>
            {(bills?.rows || []).map((row) => (
              <tr key={row.document_id}>
                <td className="py-2 pr-3">
                  <div className="font-medium">{row.number}</div>
                  <Pill status="posted">{docTypeLabel(row.doc_type)}</Pill>
                </td>
                <td className="py-2 pr-3 text-slate-600">{row.due_date || row.date}</td>
                <td className="py-2 pr-3 text-slate-600">{row.counterparty_name || "-"}</td>
                <td className="py-2 pr-3 text-right"><Money minor={row.open_minor} /></td>
                <td className="py-2 pr-3 text-right">
                  {canExpense && (
                    <Button variant="secondary" data-testid={`books-pay-bill-${row.number}`}
                            onClick={() => setForm({ kind: "bill_payment", row })}>Pay</Button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        </Section>
      )}

      {tab === "vendors" && (
        <Section title="Vendors" testId="books-vendors"
                 actions={canExpense && <Button variant="secondary" onClick={() => setForm("vendor")}
                                                data-testid="books-new-vendor">
                            <Plus className="w-4 h-4" /> Add vendor
                          </Button>}>
          <Table head={["Name", "Contact", "Category"]}
                 empty={vendors.length === 0 && <Empty>No vendors yet.</Empty>}>
            {vendors.map((v) => (
              <tr key={v.id}>
                <td className="py-2 pr-3 font-medium">{v.name}</td>
                <td className="py-2 pr-3 text-slate-600">{v.contact || "-"}</td>
                <td className="py-2 pr-3 text-slate-600">{v.category || "-"}</td>
              </tr>
            ))}
          </Table>
        </Section>
      )}

      {form && (
        <EntryModal form={form} accounts={accounts} vendors={vendors} onClose={() => setForm(null)} onDone={done} />
      )}
    </div>
  );
}

function EntryModal({ form, accounts, vendors, onClose, onDone }) {
  const kind = typeof form === "string" ? form : form.kind;
  const row = typeof form === "object" ? form.row : null;
  const payment = (accounts || []).filter((a) => a.is_payment_account && a.active);
  const expenseAccounts = (accounts || []).filter((a) => a.account_type === "expense" && a.allow_manual && a.active);
  const [state, setState] = useState({
    amount: row ? (row.open_minor / 100).toFixed(2) : "", date: todayISO(), method: "cash",
    payment_account_id: "", category: "fuel", vendor_id: "", description: "", reference: "",
    due_date: "", driver_id: "", account_id: "", kind: "owner_contribution", from_account_id: "",
    to_account_id: "", insurer_name: "", claim_reference: "", evidence_id: "", fee: "",
  });
  const [drivers, setDrivers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (["driver_accrual", "driver_payment"].includes(kind)) {
      api.get("/drivers").then(({ data }) => setDrivers(data?.items || data || [])).catch(() => {});
    }
    if (kind === "insurance") {
      api.get("/administration/documents").then(({ data }) => setDocuments(data.items || [])).catch(() => {});
    }
  }, [kind]);

  const submit = async () => {
    setBusy(true);
    const headers = { headers: idemHeaders() };
    try {
      if (kind === "expense") {
        await api.post("/finance/expenses", {
          category: state.category, amount: state.amount, date: state.date, method: state.method,
          payment_account_id: state.payment_account_id || null, vendor_id: state.vendor_id || null,
          description: state.description, reference: state.reference || null }, headers);
      } else if (kind === "bill") {
        await api.post("/finance/bills", {
          category: state.category, vendor_id: state.vendor_id, amount: state.amount, date: state.date,
          due_date: state.due_date || null, description: state.description }, headers);
      } else if (kind === "bill_payment") {
        const path = row.doc_type === "driver_accrual" ? "/finance/driver-pay/payments"
                                                       : `/finance/bills/${row.document_id}/payments`;
        const body = row.doc_type === "driver_accrual"
          ? { accrual_id: row.document_id, amount: state.amount, date: state.date, method: state.method,
              payment_account_id: state.payment_account_id || null }
          : { amount: state.amount, date: state.date, method: state.method,
              payment_account_id: state.payment_account_id || null };
        await api.post(path, body, headers);
      } else if (kind === "transfer") {
        await api.post("/finance/transfers", {
          from_account_id: state.from_account_id, to_account_id: state.to_account_id, amount: state.amount,
          fee: state.fee || null, date: state.date, description: state.description }, headers);
      } else if (kind === "other_income") {
        await api.post("/finance/other-income", {
          amount: state.amount, date: state.date, method: state.method,
          payment_account_id: state.payment_account_id || null, description: state.description,
          category: state.category }, headers);
      } else if (kind === "owner") {
        await api.post("/finance/owner-transactions", {
          kind: state.kind, amount: state.amount, date: state.date, method: state.method,
          payment_account_id: state.payment_account_id || null, description: state.description }, headers);
      } else if (kind === "driver_accrual") {
        await api.post("/finance/driver-pay/accruals", {
          driver_id: state.driver_id, amount: state.amount, date: state.date,
          description: state.description || "Driver pay" }, headers);
      } else if (kind === "driver_payment") {
        await api.post("/finance/driver-pay/payments", {
          driver_id: state.driver_id, amount: state.amount, date: state.date, method: state.method,
          payment_account_id: state.payment_account_id || null,
          description: state.description || "Driver pay" }, headers);
      } else if (kind === "insurance") {
        await api.post("/finance/insurance-claims", {
          insurer_name: state.insurer_name, claim_reference: state.claim_reference, amount: state.amount,
          date: state.date,
          evidence_refs: state.evidence_id ? [{ type: "business_document", id: state.evidence_id }] : [],
        }, headers);
      } else if (kind === "vendor") {
        await api.post("/finance/vendors", { name: state.description, contact: state.reference || null });
      }
      toast.success("Saved.");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (patch) => setState({ ...state, ...patch });
  const amountField = (
    <Field label="Amount (₱)">
      <input className={inputClass} inputMode="decimal" value={state.amount} data-testid="books-entry-amount"
             onChange={(e) => set({ amount: e.target.value })} />
    </Field>
  );
  const dateField = (
    <Field label="Date">
      <input type="date" className={inputClass} value={state.date} onChange={(e) => set({ date: e.target.value })} />
    </Field>
  );
  const methodFields = (
    <>
      <Field label="Paid with">
        <select className={inputClass} value={state.method} onChange={(e) => set({ method: e.target.value })}>
          {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </Field>
      <Field label="Account">
        <select className={inputClass} value={state.payment_account_id} data-testid="books-entry-account"
                onChange={(e) => set({ payment_account_id: e.target.value })}>
          <option value="">Use my settings</option>
          {payment.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
        </select>
      </Field>
    </>
  );

  return (
    <Modal title={FORMS[kind] || (kind === "bill_payment" ? `Pay ${row?.number}` : "Add vendor")}
           onClose={onClose} testId="books-entry-modal">
      <div className="grid sm:grid-cols-2 gap-3">
        {kind === "vendor" ? (
          <>
            <Field label="Vendor name" className="sm:col-span-2">
              <input className={inputClass} value={state.description} data-testid="books-vendor-name"
                     onChange={(e) => set({ description: e.target.value })} />
            </Field>
            <Field label="Contact (optional)" className="sm:col-span-2">
              <input className={inputClass} value={state.reference}
                     onChange={(e) => set({ reference: e.target.value })} />
            </Field>
          </>
        ) : (
          <>
            {amountField}
            {dateField}
            {["expense", "bill", "other_income"].includes(kind) && (
              <Field label={kind === "other_income" ? "Category (optional)" : "Category"}>
                <select className={inputClass} value={state.category} data-testid="books-entry-category"
                        onChange={(e) => set({ category: e.target.value })}>
                  {EXPENSE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </Field>
            )}
            {["expense", "bill"].includes(kind) && (
              <Field label={kind === "bill" ? "Vendor" : "Vendor (optional)"}>
                <select className={inputClass} value={state.vendor_id} data-testid="books-entry-vendor"
                        onChange={(e) => set({ vendor_id: e.target.value })}>
                  <option value="">-</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </Field>
            )}
            {kind === "bill" && (
              <Field label="Due date (optional)">
                <input type="date" className={inputClass} value={state.due_date}
                       onChange={(e) => set({ due_date: e.target.value })} />
              </Field>
            )}
            {["expense", "bill_payment", "other_income", "owner", "driver_payment"].includes(kind) && methodFields}
            {kind === "transfer" && (
              <>
                <Field label="From">
                  <select className={inputClass} value={state.from_account_id} data-testid="books-transfer-from"
                          onChange={(e) => set({ from_account_id: e.target.value })}>
                    <option value="">Select</option>
                    {payment.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
                  </select>
                </Field>
                <Field label="To">
                  <select className={inputClass} value={state.to_account_id} data-testid="books-transfer-to"
                          onChange={(e) => set({ to_account_id: e.target.value })}>
                    <option value="">Select</option>
                    {payment.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
                  </select>
                </Field>
                <Field label="Fee kept by the bank (optional)">
                  <input className={inputClass} inputMode="decimal" value={state.fee}
                         onChange={(e) => set({ fee: e.target.value })} />
                </Field>
              </>
            )}
            {kind === "owner" && (
              <Field label="What happened">
                <select className={inputClass} value={state.kind} onChange={(e) => set({ kind: e.target.value })}>
                  <option value="owner_contribution">Owner put money in</option>
                  <option value="owner_drawing">Owner took money out</option>
                  <option value="loan_received">Loan received</option>
                  <option value="loan_repayment">Loan repaid</option>
                </select>
              </Field>
            )}
            {["driver_accrual", "driver_payment"].includes(kind) && (
              <Field label="Driver">
                <select className={inputClass} value={state.driver_id} data-testid="books-entry-driver"
                        onChange={(e) => set({ driver_id: e.target.value })}>
                  <option value="">Select a driver</option>
                  {drivers.map((d) => (
                    <option key={d.id} value={d.id}>{d.driver_display_name || d.name || d.id}</option>
                  ))}
                </select>
              </Field>
            )}
            {kind === "insurance" && (
              <>
                <Field label="Insurer">
                  <input className={inputClass} value={state.insurer_name} data-testid="books-insurer"
                         onChange={(e) => set({ insurer_name: e.target.value })} />
                </Field>
                <Field label="Claim reference">
                  <input className={inputClass} value={state.claim_reference}
                         onChange={(e) => set({ claim_reference: e.target.value })} />
                </Field>
                <Field label="Approval evidence" className="sm:col-span-2"
                       hint="The insurer's written approval, kept in your business documents.">
                  <select className={inputClass} value={state.evidence_id} data-testid="books-insurance-evidence"
                          onChange={(e) => set({ evidence_id: e.target.value })}>
                    <option value="">Select a document</option>
                    {documents.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
                  </select>
                </Field>
              </>
            )}
            {kind !== "insurance" && (
              <Field label="Description" className="sm:col-span-2">
                <input className={inputClass} value={state.description} data-testid="books-entry-description"
                       onChange={(e) => set({ description: e.target.value })} />
              </Field>
            )}
          </>
        )}
      </div>
      <Button onClick={submit} busy={busy} data-testid="books-entry-save">Save</Button>
    </Modal>
  );
}
